<template>
  <div class="groups">

    <header class="masthead">
      <div>
        <h1 class="title">Company groups<span class="dot">.</span></h1>
        <p class="sub">
          Which company each person reports under. Statistics can be filtered
          by group. Everybody not placed in a group is in the default group,
          so a new person needs no action. The dashboard and the display
          screens do not change.
        </p>
      </div>
    </header>

    <p v-if="loading" class="msg">Reading groups and people</p>

    <p v-else-if="error" class="msg err">
      {{ error }}
      <button class="link" @click="load">Try again</button>
    </p>

    <template v-else>

      <!-- ---------- Groups ---------- -->
      <section class="block">
        <h2 class="section-title">Groups</h2>

        <table class="table groups-table">
          <thead>
            <tr>
              <th class="col-gname">Name</th>
              <th class="col-gcount">People</th>
              <th class="col-gdefault">Default</th>
              <th class="col-gactions"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="g in groups" :key="g.id">
              <td>
                <!-- Rename in place: Enter or blur saves, Escape reverts. -->
                <input
                  class="name-input"
                  type="text"
                  :value="nameDraft[g.id] ?? g.name"
                  @input="nameDraft[g.id] = $event.target.value"
                  @blur="rename(g)"
                  @keyup.enter="$event.target.blur()"
                  @keyup.esc="nameDraft[g.id] = g.name; $event.target.blur()"
                >
              </td>
              <td class="num">{{ countFor(g) }}</td>
              <td>
                <span v-if="g.is_default" class="tag-default">default</span>
                <button v-else class="link" @click="makeDefault(g)">Make default</button>
              </td>
              <td class="actions">
                <span v-if="groupState[g.id] === 'saving'" class="pending">saving</span>
                <span v-else-if="groupState[g.id] === 'saved'" class="ok">saved</span>
                <span v-else-if="groupState[g.id]" class="bad">{{ groupState[g.id] }}</span>
                <!-- The default cannot go. The backend refuses it too; the
                     button is simply not offered rather than offered and
                     then refused. -->
                <button
                  v-if="!g.is_default"
                  class="link danger"
                  @click="remove(g)"
                >Delete</button>
              </td>
            </tr>
          </tbody>
        </table>

        <form class="add-row" @submit.prevent="add">
          <input
            v-model="newName"
            class="name-input add-input"
            type="text"
            placeholder="New group name"
            maxlength="100"
            aria-label="New group name"
          >
          <button class="btn primary" type="submit" :disabled="!newName.trim() || adding">
            {{ adding ? 'Adding…' : 'Add group' }}
          </button>
          <span v-if="addError" class="bad">{{ addError }}</span>
        </form>
      </section>

      <!-- ---------- People ---------- -->
      <section class="block">
        <h2 class="section-title">People</h2>

        <div class="controls">
          <input
            v-model="query"
            class="search"
            type="search"
            placeholder="Search name or email..."
            aria-label="Search"
          >

          <label class="filter">
            <span class="filter-label">Show</span>
            <select v-model="showGroup" class="select">
              <option :value="0">Everyone</option>
              <option v-for="g in groups" :key="g.id" :value="g.id">{{ g.name }}</option>
            </select>
          </label>

          <span class="count">{{ visible.length }} {{ visible.length === 1 ? 'person' : 'people' }}</span>
        </div>

        <p v-if="visible.length === 0" class="msg">Nobody matches</p>

        <table v-else class="table">
          <thead>
            <tr>
              <th class="col-name">Name</th>
              <th class="col-email">Email</th>
              <th class="col-group">Company</th>
              <th class="col-state"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="p in visible" :key="p.id">
              <td class="name">{{ p.name }}</td>
              <td class="email">{{ p.email || '—' }}</td>
              <td>
                <!-- Saved on change. The default is chosen by its own id;
                     the backend turns that into "no explicit placement". -->
                <select
                  class="select"
                  :value="groupOf(p.id)"
                  @change="assign(p, Number($event.target.value))"
                >
                  <option v-for="g in groups" :key="g.id" :value="g.id">
                    {{ g.name }}{{ g.is_default ? ' (default)' : '' }}
                  </option>
                </select>
              </td>
              <td class="state">
                <span v-if="personState[p.id] === 'saving'" class="pending">saving</span>
                <span v-else-if="personState[p.id] === 'saved'" class="ok">saved</span>
                <span v-else-if="personState[p.id]" class="bad">{{ personState[p.id] }}</span>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Placed people who have left the directory. They cannot appear
             above, so without this they could never be removed. -->
        <div v-if="orphans.length > 0" class="orphans">
          <h3 class="section-title">Placed, but no longer in the directory</h3>
          <ul class="orphan-list">
            <li v-for="m in orphans" :key="m.entra_object_id">
              <span class="orphan-name">{{ m.name || m.email || m.entra_object_id }}</span>
              <span class="orphan-group">{{ m.group_name }}</span>
              <button class="link danger" @click="clearOrphan(m)">Remove</button>
            </li>
          </ul>
        </div>
      </section>

    </template>

  </div>
</template>


<script setup>
import { ref, computed, onMounted } from 'vue'
import { getSiteHierarchy, getPeopleInCountry } from '@/services/GraphService'
import {
  getCompanyGroups, createCompanyGroup, renameCompanyGroup,
  setDefaultCompanyGroup, deleteCompanyGroup, setPersonCompanyGroup
} from '@/services/CompanyGroupService'

const loading = ref(true)
const error = ref(null)

const groups = ref([])
const members = ref([])     // explicit placements only
const people = ref([])      // the whole directory

const nameDraft = ref({})
const groupState = ref({})
const newName = ref('')
const adding = ref(false)
const addError = ref('')

const query = ref('')
const showGroup = ref(0)
const personState = ref({})

const defaultGroup = computed(() => groups.value.find((g) => g.is_default) ?? null)

// Person id -> explicit group id.
const placed = computed(() => {
  const map = {}
  for (const m of members.value) map[m.entra_object_id] = m.group_id
  return map
})

// Effective group: the explicit one, else the default.
function groupOf(personId) {
  return placed.value[personId] ?? defaultGroup.value?.id ?? 0
}

// The default's count is everybody not placed elsewhere, which only the
// page knows because only the page has the directory.
function countFor(g) {
  if (!g.is_default) return g.member_count
  const elsewhere = people.value.filter((p) => placed.value[p.id] && placed.value[p.id] !== g.id).length
  return Math.max(0, people.value.length - elsewhere)
}

const visible = computed(() => {
  const q = query.value.trim().toLowerCase()
  return people.value.filter((p) => {
    if (showGroup.value && groupOf(p.id) !== showGroup.value) return false
    if (!q) return true
    return [p.name, p.email].filter(Boolean).some((f) => f.toLowerCase().includes(q))
  })
})

const orphans = computed(() => {
  const known = new Set(people.value.map((p) => p.id))
  return members.value.filter((m) => !known.has(m.entra_object_id))
})

async function load() {
  loading.value = true
  error.value = null

  try {
    const [hierarchy, data] = await Promise.all([getSiteHierarchy(), getCompanyGroups()])
    groups.value = data.groups
    members.value = data.members

    // Everyone, across every country, like the Badges page: a company is a
    // property of the person, not of where they sit.
    const all = []
    for (const country of hierarchy?.countries ?? []) {
      const inCountry = await getPeopleInCountry(country.code)
      if (inCountry) all.push(...inCountry)
    }
    people.value = all.sort((a, b) => a.name.localeCompare(b.name))
    nameDraft.value = {}
  } catch (err) {
    console.error(err)
    error.value = err.message ?? String(err)
  } finally {
    loading.value = false
  }
}

// Only the groups and placements, after a change: the directory has not
// moved and is the slow part.
async function reloadGroups() {
  const data = await getCompanyGroups()
  groups.value = data.groups
  members.value = data.members
}

function flash(store, id, value) {
  store.value = { ...store.value, [id]: value }
  if (value === 'saved') {
    setTimeout(() => {
      const next = { ...store.value }
      if (next[id] === 'saved') delete next[id]
      store.value = next
    }, 1500)
  }
}

async function add() {
  const name = newName.value.trim()
  if (!name) return
  adding.value = true
  addError.value = ''
  try {
    await createCompanyGroup(name)
    newName.value = ''
    await reloadGroups()
  } catch (err) {
    addError.value = err.message ?? String(err)
  } finally {
    adding.value = false
  }
}

async function rename(g) {
  const typed = (nameDraft.value[g.id] ?? g.name).trim()
  if (!typed || typed === g.name) {
    nameDraft.value = { ...nameDraft.value, [g.id]: g.name }
    return
  }
  flash(groupState, g.id, 'saving')
  try {
    await renameCompanyGroup(g.id, typed)
    await reloadGroups()
    nameDraft.value = { ...nameDraft.value, [g.id]: typed }
    flash(groupState, g.id, 'saved')
  } catch (err) {
    nameDraft.value = { ...nameDraft.value, [g.id]: g.name }
    flash(groupState, g.id, err.message ?? String(err))
  }
}

async function makeDefault(g) {
  flash(groupState, g.id, 'saving')
  try {
    await setDefaultCompanyGroup(g.id)
    await reloadGroups()
    flash(groupState, g.id, 'saved')
  } catch (err) {
    flash(groupState, g.id, err.message ?? String(err))
  }
}

async function remove(g) {
  const n = g.member_count
  const tail = n > 0
    ? ` ${n} ${n === 1 ? 'person moves' : 'people move'} to ${defaultGroup.value?.name ?? 'the default group'}.`
    : ''
  if (!window.confirm(`Delete the group "${g.name}"?${tail}`)) return

  flash(groupState, g.id, 'saving')
  try {
    await deleteCompanyGroup(g.id)
    await reloadGroups()
  } catch (err) {
    flash(groupState, g.id, err.message ?? String(err))
  }
}

async function assign(p, groupId) {
  if (groupId === groupOf(p.id)) return
  flash(personState, p.id, 'saving')
  try {
    await setPersonCompanyGroup({ entraObjectId: p.id, groupId, email: p.email, name: p.name })
    await reloadGroups()
    flash(personState, p.id, 'saved')
  } catch (err) {
    flash(personState, p.id, err.message ?? String(err))
  }
}

async function clearOrphan(m) {
  try {
    await setPersonCompanyGroup({ entraObjectId: m.entra_object_id, groupId: 0 })
    await reloadGroups()
  } catch (err) {
    error.value = err.message ?? String(err)
  }
}

onMounted(load)
</script>


<style scoped>
.groups {
  --ink: #003c6c;
  --ink-soft: #64748b;
  --accent: #ed6e01;
  --line: #dfe4e9;
  --wash: #f7f9fb;
  --ok: #0f8b6c;

  max-width: 1100px;
  margin: 0;
  padding: 0 24px 80px;
  text-align: left;
  color: #191c1e;
}

.masthead {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  padding: 30px 0 22px;
  border-bottom: 1px solid var(--line);
  margin-bottom: 18px;
}

.title {
  font-size: clamp(28px, 3.8vw, 40px);
  font-weight: 700;
  letter-spacing: -0.03em;
  margin: 0;
  color: var(--ink);
}

.dot { color: var(--accent); }

.sub {
  font-size: 14.5px;
  line-height: 1.55;
  color: var(--ink-soft);
  margin: 8px 0 0;
  max-width: 64ch;
}

.block { margin-bottom: 36px; }

.section-title {
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--ink-soft);
  margin: 0 0 12px;
}

/* ---------- Controls ---------- */

.controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  margin-bottom: 18px;
}

.search {
  font-family: inherit;
  font-size: 15px;
  height: 44px;
  width: 320px;
  max-width: 100%;
  padding: 0 14px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #fff;
  outline: none;
}

.search:focus { border-color: var(--ink); }

.filter {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: var(--ink-soft);
}

.count {
  font-size: 14px;
  color: var(--ink-soft);
}

.select {
  font-family: inherit;
  font-size: 14px;
  height: 36px;
  padding: 0 10px;
  border: 1px solid var(--line);
  border-radius: 7px;
  background: #fff;
  color: var(--ink);
  outline: none;
  min-width: 160px;
}

.select:focus { border-color: var(--ink); }

.name-input {
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  color: var(--ink);
  height: 36px;
  width: 260px;
  max-width: 100%;
  padding: 0 10px;
  border: 1px solid transparent;
  border-radius: 7px;
  background: transparent;
  outline: none;
  box-sizing: border-box;
}

.name-input:hover { border-color: var(--line); }
.name-input:focus { border-color: var(--ink); background: #fff; }

.add-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-top: 14px;
}

.add-input {
  border-color: var(--line);
  background: #fff;
  font-weight: 400;
}

.btn {
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  height: 36px;
  padding: 0 18px;
  border: 1px solid var(--ink);
  border-radius: 8px;
  background: transparent;
  color: var(--ink);
  cursor: pointer;
}

.btn:disabled { opacity: 0.4; cursor: default; }
.btn.primary { background: var(--ink); color: #fff; }
.btn.primary:hover:not(:disabled) { background: #00548f; }

/* ---------- Tables ---------- */

.table {
  width: 100%;
  border-collapse: collapse;
  font-size: 15px;
}

.table th {
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--ink-soft);
  padding: 0 12px 10px 0;
  border-bottom: 1px solid var(--line);
  background: #fff;
}

.table td {
  padding: 7px 12px 7px 0;
  border-bottom: 1px solid #f1f4f7;
  vertical-align: middle;
}

.groups-table { max-width: 760px; }

.col-gname { width: 44%; }
.col-gcount { width: 14%; }
.col-gdefault { width: 20%; }
.col-gactions { width: 22%; }

.col-name { width: 28%; }
.col-email { width: 32%; }
.col-group { width: 26%; }
.col-state { width: 14%; }

.num { font-variant-numeric: tabular-nums; color: var(--ink-soft); }

.name { font-weight: 600; color: var(--ink); }

.email {
  color: var(--ink-soft);
  font-size: 14px;
}

.actions {
  display: flex;
  align-items: center;
  gap: 14px;
  justify-content: flex-end;
  font-size: 12.5px;
}

.tag-default {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink);
  background: var(--wash);
  border: 1px solid var(--line);
  border-radius: 6px;
  padding: 3px 8px;
}

/* ---------- Row state ---------- */

.state {
  font-size: 12.5px;
  white-space: nowrap;
}

.pending { color: var(--ink-soft); }
.ok { color: var(--ok); font-weight: 600; }
.bad { color: #b3261e; font-size: 12.5px; }

.link {
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: var(--ink);
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.link.danger { color: #b3261e; }

/* ---------- Orphans ---------- */

.orphans {
  margin-top: 28px;
  padding-top: 18px;
  border-top: 1px solid var(--line);
}

.orphan-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 14px;
}

.orphan-list li {
  display: flex;
  align-items: center;
  gap: 16px;
}

.orphan-name { color: var(--ink); font-weight: 600; }
.orphan-group { color: var(--ink-soft); }

/* ---------- Messages ---------- */

.msg {
  font-size: 15px;
  color: var(--ink-soft);
  padding: 20px 0;
}

.msg.err { color: #b3261e; }
</style>
